import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  StatusBar,
  RefreshControl,
  Modal,
  FlatList,
  Alert,
  Animated,
  ActivityIndicator,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width, height } = Dimensions.get('window');

// JobPostAnalytics Component
export default function JobPostAnalytics({ navigation, route }) {
  // Core state management
  const [isLoading, setIsLoading] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');
  const [selectedMetric, setSelectedMetric] = useState('overview');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedJobPost, setSelectedJobPost] = useState(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  
  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Analytics data structure
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalJobPosts: 24,
      activeJobPosts: 18,
      totalApplications: 456,
      totalViews: 12847,
      averageApplicationsPerJob: 19,
      conversionRate: 3.5,
      topPerformingJob: 'Senior Software Engineer',
      totalHires: 8,
      timeToHire: 14, // days
      costPerHire: 2500,
    },
    timeframeData: {
      '7days': {
        applications: [12, 18, 15, 22, 19, 25, 21],
        views: [145, 189, 167, 234, 198, 267, 223],
        posts: [2, 1, 0, 3, 1, 2, 0],
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      },
      '30days': {
        applications: [89, 124, 98, 156, 134, 178, 145, 167, 189, 201, 156, 178, 134, 189, 167, 201, 178, 156, 189, 167, 201, 178, 156, 189, 167, 201, 178, 156, 189, 167],
        views: [1245, 1567, 1234, 1789, 1456, 1890, 1567, 1678, 1789, 1890, 1567, 1789, 1456, 1890, 1678, 1890, 1789, 1567, 1890, 1678, 1890, 1789, 1567, 1890, 1678, 1890, 1789, 1567, 1890, 1678],
        posts: [1, 2, 0, 1, 3, 1, 0, 2, 1, 0, 2, 1, 3, 0, 1, 2, 0, 1, 2, 0, 1, 2, 0, 1, 2, 0, 1, 2, 0, 1],
        labels: Array.from({length: 30}, (_, i) => `${i + 1}`)
      },
      '90days': {
        applications: Array.from({length: 90}, (_, i) => Math.floor(Math.random() * 200) + 50),
        views: Array.from({length: 90}, (_, i) => Math.floor(Math.random() * 2000) + 500),
        posts: Array.from({length: 90}, (_, i) => Math.floor(Math.random() * 4)),
        labels: Array.from({length: 90}, (_, i) => `${i + 1}`)
      }
    },
    jobPostsPerformance: [
      {
        id: 1,
        title: 'Senior Software Engineer',
        department: 'Engineering',
        postedDate: '2024-08-15',
        status: 'Active',
        applications: 89,
        views: 2847,
        conversionRate: 3.1,
        averageRating: 4.8,
        timeToFill: 12,
        budget: 85000,
        location: 'London, UK',
        type: 'Full-time',
        urgency: 'High',
        performance: 'Excellent',
        hires: 2,
        interviews: 12,
        shortlisted: 18,
        rejected: 59,
        pending: 12,
        sourceBreakdown: {
          direct: 34,
          linkedin: 28,
          jobboards: 18,
          referrals: 9
        },
        skillsMatch: {
          excellent: 23,
          good: 31,
          fair: 25,
          poor: 10
        },
        experienceLevel: {
          senior: 45,
          mid: 32,
          junior: 12
        }
      },
      {
        id: 2,
        title: 'Project Manager',
        department: 'Operations',
        postedDate: '2024-08-20',
        status: 'Active',
        applications: 67,
        views: 1934,
        conversionRate: 3.5,
        averageRating: 4.6,
        timeToFill: 18,
        budget: 65000,
        location: 'Manchester, UK',
        type: 'Full-time',
        urgency: 'Medium',
        performance: 'Good',
        hires: 1,
        interviews: 8,
        shortlisted: 15,
        rejected: 43,
        pending: 9,
        sourceBreakdown: {
          direct: 25,
          linkedin: 22,
          jobboards: 15,
          referrals: 5
        },
        skillsMatch: {
          excellent: 18,
          good: 28,
          fair: 15,
          poor: 6
        },
        experienceLevel: {
          senior: 35,
          mid: 25,
          junior: 7
        }
      },
      {
        id: 3,
        title: 'UX/UI Designer',
        department: 'Design',
        postedDate: '2024-08-25',
        status: 'Active',
        applications: 124,
        views: 3456,
        conversionRate: 3.6,
        averageRating: 4.9,
        timeToFill: 8,
        budget: 55000,
        location: 'Remote',
        type: 'Full-time',
        urgency: 'High',
        performance: 'Excellent',
        hires: 3,
        interviews: 18,
        shortlisted: 28,
        rejected: 73,
        pending: 20,
        sourceBreakdown: {
          direct: 48,
          linkedin: 35,
          jobboards: 28,
          referrals: 13
        },
        skillsMatch: {
          excellent: 35,
          good: 42,
          fair: 32,
          poor: 15
        },
        experienceLevel: {
          senior: 38,
          mid: 52,
          junior: 34
        }
      },
      {
        id: 4,
        title: 'Data Analyst',
        department: 'Analytics',
        postedDate: '2024-09-01',
        status: 'Active',
        applications: 78,
        views: 2156,
        conversionRate: 3.6,
        averageRating: 4.7,
        timeToFill: 15,
        budget: 48000,
        location: 'Birmingham, UK',
        type: 'Full-time',
        urgency: 'Medium',
        performance: 'Good',
        hires: 1,
        interviews: 9,
        shortlisted: 16,
        rejected: 52,
        pending: 10,
        sourceBreakdown: {
          direct: 28,
          linkedin: 25,
          jobboards: 18,
          referrals: 7
        },
        skillsMatch: {
          excellent: 22,
          good: 31,
          fair: 18,
          poor: 7
        },
        experienceLevel: {
          senior: 28,
          mid: 35,
          junior: 15
        }
      },
      {
        id: 5,
        title: 'Marketing Specialist',
        department: 'Marketing',
        postedDate: '2024-09-05',
        status: 'Paused',
        applications: 45,
        views: 1567,
        conversionRate: 2.9,
        averageRating: 4.4,
        timeToFill: 22,
        budget: 42000,
        location: 'Leeds, UK',
        type: 'Full-time',
        urgency: 'Low',
        performance: 'Average',
        hires: 0,
        interviews: 5,
        shortlisted: 8,
        rejected: 32,
        pending: 5,
        sourceBreakdown: {
          direct: 18,
          linkedin: 15,
          jobboards: 9,
          referrals: 3
        },
        skillsMatch: {
          excellent: 12,
          good: 18,
          fair: 12,
          poor: 3
        },
        experienceLevel: {
          senior: 15,
          mid: 22,
          junior: 8
        }
      }
    ],
    insights: [
      {
        id: 1,
        type: 'success',
        title: 'High Performance Alert',
        message: 'UX/UI Designer position is performing 40% above average with excellent conversion rates.',
        action: 'Consider similar posting strategies',
        priority: 'high',
        date: '2024-09-10'
      },
      {
        id: 2,
        type: 'warning',
        title: 'Low Application Rate',
        message: 'Marketing Specialist position has 35% fewer applications than similar roles.',
        action: 'Review job description and requirements',
        priority: 'medium',
        date: '2024-09-09'
      },
      {
        id: 3,
        type: 'info',
        title: 'Trending Skills',
        message: 'React and Node.js skills are in high demand. Consider highlighting these in tech roles.',
        action: 'Update job descriptions',
        priority: 'low',
        date: '2024-09-08'
      },
      {
        id: 4,
        type: 'success',
        title: 'Fast Hiring Success',
        message: 'Average time to hire decreased by 25% this month compared to last month.',
        action: 'Document successful processes',
        priority: 'high',
        date: '2024-09-07'
      }
    ],
    demographics: {
      ageGroups: {
        '18-25': 15,
        '26-35': 45,
        '36-45': 28,
        '46-55': 10,
        '55+': 2
      },
      experience: {
        'Entry Level': 20,
        'Mid Level': 45,
        'Senior Level': 30,
        'Executive': 5
      },
      education: {
        'High School': 8,
        'Bachelor\'s': 52,
        'Master\'s': 35,
        'PhD': 5
      },
      locations: {
        'London': 35,
        'Manchester': 20,
        'Birmingham': 15,
        'Leeds': 12,
        'Remote': 18
      }
    },
    benchmarks: {
      industryAverage: {
        applicationsPerJob: 15,
        conversionRate: 2.8,
        timeToHire: 21,
        costPerHire: 3200
      },
      yourPerformance: {
        applicationsPerJob: 19,
        conversionRate: 3.5,
        timeToHire: 14,
        costPerHire: 2500
      }
    }
  });

  useEffect(() => {
    // Animate component entrance
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
    loadCompanyProfile();
  }, []);

  const loadCompanyProfile = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') ||
                    await AsyncStorage.getItem('token');
      const res = await fetch(
        `https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp && typeof cp === 'object') {
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  const onRefresh = async () => {
    setRefreshing(true);
    
    // Simulate data refresh
    setTimeout(() => {
      // Update some random data to show refresh
      setAnalyticsData(prev => ({
        ...prev,
        overview: {
          ...prev.overview,
          totalApplications: prev.overview.totalApplications + Math.floor(Math.random() * 10),
          totalViews: prev.overview.totalViews + Math.floor(Math.random() * 100),
        }
      }));
      setRefreshing(false);
    }, 1000);
  };

  const navigateToScreen = (screenName, params = {}) => {
    if (navigation) {
      navigation.navigate(screenName, params);
    } else {
      Alert.alert('Navigation', `Would navigate to ${screenName}`);
    }
  };

  const viewJobPostDetails = (jobPost) => {
    setSelectedJobPost(jobPost);
    setShowDetailsModal(true);
  };

  const getPerformanceColor = (performance) => {
    switch (performance.toLowerCase()) {
      case 'excellent': return '#10b981';
      case 'good': return '#3b82f6';
      case 'average': return '#f59e0b';
      case 'poor': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency.toLowerCase()) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#4A90E2', '#357ABD']}
      style={styles.header}
    >
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      
      <View style={[styles.headerContent, { position: 'relative' }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation?.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        
          {/* Company logo synced from CompanyProfileScreen */}
          <TouchableOpacity
            onPress={() => navigation?.navigate('CompanyProfileScreen')}
            style={{ position: 'absolute', left: 56, top: 0, bottom: 0, justifyContent: 'center' }}
          >
            {companyLogo
              ? <Image
                  source={{ uri: companyLogo || undefined }}
                  style={{ width: 34, height: 34, borderRadius: 17,
                    borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' }}
                  resizeMode="cover"
                />
              : <View style={{ width: 34, height: 34, borderRadius: 17,
                  backgroundColor: 'rgba(255,255,255,0.2)', borderWidth: 2,
                  borderColor: 'rgba(255,255,255,0.4)',
                  alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>
                    {(companyName || 'CO').split(' ').slice(0, 2)
                      .map(w => w[0]?.toUpperCase() || '').join('') || 'CO'}
                  </Text>
                </View>}
          </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Job Post Analytics</Text>
          <Text style={[styles.headerSubtitle]}>
            {companyName || 'Performance insights and metrics'}
          </Text>
        </View>
        
        <TouchableOpacity 
          style={styles.filterButton}
          onPress={() => setShowFiltersModal(true)}
        >
          <MaterialCommunityIcons name="filter-variant" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Timeframe Selector */}
      <View style={styles.timeframeSelector}>
        {['7days', '30days', '90days'].map((timeframe) => (
          <TouchableOpacity
            key={timeframe}
            style={[
              styles.timeframeButton,
              selectedTimeframe === timeframe && styles.activeTimeframeButton
            ]}
            onPress={() => setSelectedTimeframe(timeframe)}
          >
            <Text style={[
              styles.timeframeButtonText,
              selectedTimeframe === timeframe && styles.activeTimeframeButtonText
            ]}>
              {timeframe === '7days' ? '7 Days' : 
               timeframe === '30days' ? '30 Days' : '90 Days'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </LinearGradient>
  );

  const renderOverviewMetrics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Overview Metrics</Text>
      
      <View style={styles.metricsGrid}>
        <View style={[styles.metricCard, { backgroundColor: '#EEF2FF' }]}>
          <View style={[styles.metricIcon, { backgroundColor: '#8B5CF6' }]}>
            <MaterialCommunityIcons name="briefcase-plus" size={24} color="#fff" />
          </View>
          <Text style={styles.metricNumber}>{analyticsData.overview.totalJobPosts}</Text>
          <Text style={styles.metricLabel}>Total Job Posts</Text>
          <Text style={styles.metricSubtext}>{analyticsData.overview.activeJobPosts} active</Text>
        </View>

        <View style={[styles.metricCard, { backgroundColor: '#ECFDF5' }]}>
          <View style={[styles.metricIcon, { backgroundColor: '#10B981' }]}>
            <MaterialCommunityIcons name="account-group" size={24} color="#fff" />
          </View>
          <Text style={styles.metricNumber}>{analyticsData.overview.totalApplications}</Text>
          <Text style={styles.metricLabel}>Total Applications</Text>
          <Text style={styles.metricSubtext}>Avg: {analyticsData.overview.averageApplicationsPerJob}/job</Text>
        </View>

        <View style={[styles.metricCard, { backgroundColor: '#FEF3C7' }]}>
          <View style={[styles.metricIcon, { backgroundColor: '#F59E0B' }]}>
            <MaterialCommunityIcons name="eye" size={24} color="#fff" />
          </View>
          <Text style={styles.metricNumber}>{analyticsData.overview.totalViews.toLocaleString()}</Text>
          <Text style={styles.metricLabel}>Total Views</Text>
          <Text style={styles.metricSubtext}>High visibility</Text>
        </View>

        <View style={[styles.metricCard, { backgroundColor: '#F0F9FF' }]}>
          <View style={[styles.metricIcon, { backgroundColor: '#0EA5E9' }]}>
            <MaterialCommunityIcons name="percent" size={24} color="#fff" />
          </View>
          <Text style={styles.metricNumber}>{analyticsData.overview.conversionRate}%</Text>
          <Text style={styles.metricLabel}>Conversion Rate</Text>
          <Text style={styles.metricSubtext}>Above average</Text>
        </View>
      </View>

      {/* Additional Key Metrics */}
      <View style={styles.additionalMetrics}>
        <View style={styles.additionalMetricCard}>
          <MaterialCommunityIcons name="account-check" size={20} color="#10b981" />
          <Text style={styles.additionalMetricNumber}>{analyticsData.overview.totalHires}</Text>
          <Text style={styles.additionalMetricLabel}>Total Hires</Text>
        </View>
        
        <View style={styles.additionalMetricCard}>
          <MaterialCommunityIcons name="clock-outline" size={20} color="#3b82f6" />
          <Text style={styles.additionalMetricNumber}>{analyticsData.overview.timeToHire}</Text>
          <Text style={styles.additionalMetricLabel}>Avg Days to Hire</Text>
        </View>
        
        <View style={styles.additionalMetricCard}>
          <MaterialCommunityIcons name="currency-gbp" size={20} color="#f59e0b" />
          <Text style={styles.additionalMetricNumber}>£{analyticsData.overview.costPerHire}</Text>
          <Text style={styles.additionalMetricLabel}>Cost per Hire</Text>
        </View>
      </View>
    </View>
  );

  const renderSimpleChart = () => {
    const data = analyticsData.timeframeData[selectedTimeframe];
    const maxValue = Math.max(...data.applications);
    
    return (
      <View style={styles.chartContainer}>
        <Text style={styles.sectionTitle}>Applications Trend</Text>
        <Text style={styles.chartSubtitle}>
          {selectedTimeframe === '7days' ? 'Last 7 days' : 
           selectedTimeframe === '30days' ? 'Last 30 days' : 'Last 90 days'}
        </Text>
        
        <View style={styles.simpleChart}>
          <View style={styles.chartBars}>
            {data.applications.slice(-10).map((value, index) => (
              <View key={index} style={styles.chartBarContainer}>
                <View 
                  style={[
                    styles.chartBar,
                    { 
                      height: (value / maxValue) * 100,
                      backgroundColor: index === data.applications.slice(-10).length - 1 ? '#4A90E2' : '#E5E7EB'
                    }
                  ]} 
                />
                <Text style={styles.chartBarLabel}>
                  {selectedTimeframe === '7days' ? data.labels[index] : index + 1}
                </Text>
              </View>
            ))}
          </View>
          
          <View style={styles.chartLegend}>
            <View style={styles.chartLegendItem}>
              <View style={[styles.chartLegendColor, { backgroundColor: '#4A90E2' }]} />
              <Text style={styles.chartLegendText}>Applications</Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderJobPostsPerformance = () => (
    <View style={styles.performanceContainer}>
      <View style={styles.performanceHeader}>
        <Text style={styles.sectionTitle}>Job Posts Performance</Text>
        <TouchableOpacity 
          style={styles.viewAllButton}
          onPress={() => navigateToScreen('AllJobPostsScreen')}
        >
          <Text style={styles.viewAllButtonText}>View All</Text>
          <Ionicons name="chevron-forward" size={16} color="#4A90E2" />
        </TouchableOpacity>
      </View>

      {analyticsData.jobPostsPerformance.map((item) => (
          <TouchableOpacity 
            key={item.id.toString()}
            style={styles.jobPostCard}
            onPress={() => viewJobPostDetails(item)}
          >
            <View style={styles.jobPostHeader}>
              <View style={styles.jobPostTitleContainer}>
                <Text style={styles.jobPostTitle}>{item.title}</Text>
                <Text style={styles.jobPostDepartment}>{item.department}</Text>
              </View>
              
              <View style={styles.jobPostBadges}>
                <View style={[
                  styles.performanceBadge,
                  { backgroundColor: getPerformanceColor(item.performance) }
                ]}>
                  <Text style={styles.performanceBadgeText}>{item.performance}</Text>
                </View>
                
                <View style={[
                  styles.urgencyBadge,
                  { backgroundColor: getUrgencyColor(item.urgency) }
                ]}>
                  <Text style={styles.urgencyBadgeText}>{item.urgency}</Text>
                </View>
              </View>
            </View>

            <View style={styles.jobPostMetrics}>
              <View style={styles.jobPostMetric}>
                <MaterialCommunityIcons name="account-group" size={16} color="#6b7280" />
                <Text style={styles.jobPostMetricText}>{item.applications} applications</Text>
              </View>
              
              <View style={styles.jobPostMetric}>
                <MaterialCommunityIcons name="eye" size={16} color="#6b7280" />
                <Text style={styles.jobPostMetricText}>{item.views} views</Text>
              </View>
              
              <View style={styles.jobPostMetric}>
                <MaterialCommunityIcons name="percent" size={16} color="#6b7280" />
                <Text style={styles.jobPostMetricText}>{item.conversionRate}% conversion</Text>
              </View>
            </View>

            <View style={styles.jobPostProgress}>
              <View style={styles.progressInfo}>
                <Text style={styles.progressLabel}>Hiring Progress</Text>
                <Text style={styles.progressText}>{item.hires} hired / {item.interviews} interviewed</Text>
              </View>
              
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill,
                    { width: `${(item.hires / (item.applications || 1)) * 100}%` }
                  ]} 
                />
              </View>
            </View>

            <View style={styles.jobPostFooter}>
              <Text style={styles.jobPostDate}>Posted: {item.postedDate}</Text>
              <Text style={styles.jobPostLocation}>{item.location}</Text>
            </View>
          </TouchableOpacity>
        ))}
    </View>
  );

  const renderInsights = () => (
    <View style={styles.insightsContainer}>
      <Text style={styles.sectionTitle}>AI-Powered Insights</Text>
      <Text style={styles.sectionSubtitle}>Actionable recommendations to improve your hiring</Text>

      {analyticsData.insights.map((insight) => (
        <View key={insight.id} style={styles.insightCard}>
          <View style={styles.insightHeader}>
            <View style={[
              styles.insightIcon,
              { backgroundColor: 
                insight.type === 'success' ? '#10b981' :
                insight.type === 'warning' ? '#f59e0b' :
                insight.type === 'info' ? '#3b82f6' : '#6b7280'
              }
            ]}>
              <MaterialCommunityIcons 
                name={
                  insight.type === 'success' ? 'check-circle' :
                  insight.type === 'warning' ? 'alert-circle' :
                  insight.type === 'info' ? 'information' : 'help-circle'
                } 
                size={20} 
                color="#fff" 
              />
            </View>
            
            <View style={styles.insightContent}>
              <Text style={styles.insightTitle}>{insight.title}</Text>
              <Text style={styles.insightMessage}>{insight.message}</Text>
            </View>
            
            <View style={[
              styles.priorityIndicator,
              { backgroundColor: 
                insight.priority === 'high' ? '#ef4444' :
                insight.priority === 'medium' ? '#f59e0b' : '#10b981'
              }
            ]} />
          </View>
          
          <TouchableOpacity style={styles.insightAction}>
            <Text style={styles.insightActionText}>{insight.action}</Text>
            <Ionicons name="chevron-forward" size={16} color="#4A90E2" />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );

  const renderBenchmarks = () => (
    <View style={styles.benchmarksContainer}>
      <Text style={styles.sectionTitle}>Industry Benchmarks</Text>
      <Text style={styles.sectionSubtitle}>Compare your performance with industry standards</Text>

      <View style={styles.benchmarkGrid}>
        {Object.keys(analyticsData.benchmarks.industryAverage).map((metric) => {
          const industryValue = analyticsData.benchmarks.industryAverage[metric];
          const yourValue = analyticsData.benchmarks.yourPerformance[metric];
          const isPercentage = metric === 'conversionRate';
          const isCurrency = metric === 'costPerHire';
          const isTime = metric === 'timeToHire';
          const isBetter = metric === 'costPerHire' || metric === 'timeToHire' ? 
            yourValue < industryValue : yourValue > industryValue;

          return (
            <View key={metric} style={styles.benchmarkCard}>
              <Text style={styles.benchmarkMetric}>
                {metric.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Text>
              
              <View style={styles.benchmarkComparison}>
                <View style={styles.benchmarkValue}>
                  <Text style={styles.benchmarkLabel}>Your Performance</Text>
                  <Text style={[styles.benchmarkNumber, { color: isBetter ? '#10b981' : '#ef4444' }]}>
                    <Text>{isCurrency ? '£' : ''}{yourValue}{isPercentage ? '%' : ''}{isTime ? ' days' : ''}</Text>
                  </Text>
                </View>
                
                <View style={styles.benchmarkValue}>
                  <Text style={styles.benchmarkLabel}>Industry Average</Text>
                  <Text style={styles.benchmarkNumber}>
                    <Text>{isCurrency ? '£' : ''}{industryValue}{isPercentage ? '%' : ''}{isTime ? ' days' : ''}</Text>
                  </Text>
                </View>
              </View>
              
              <View style={styles.benchmarkIndicator}>
                <MaterialCommunityIcons 
                  name={isBetter ? 'trending-up' : 'trending-down'} 
                  size={16} 
                  color={isBetter ? '#10b981' : '#ef4444'} 
                />
                <Text style={[styles.benchmarkStatus, { color: isBetter ? '#10b981' : '#ef4444' }]}>
                  <Text>{isBetter ? 'Above Average' : 'Below Average'}</Text>
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );

  const renderJobPostDetailsModal = () => (
    <Modal
      animationType="slide"
      transparent={false}
      visible={showDetailsModal}
      onRequestClose={() => setShowDetailsModal(false)}
    >
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.fullScreenContainer}>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <SafeAreaView style={styles.safeContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity 
              style={styles.modalBackButton}
              onPress={() => setShowDetailsModal(false)}
            >
              <Ionicons name="arrow-back" size={24} color="#fff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>
              {selectedJobPost ? selectedJobPost.title : 'Job Details'}
            </Text>
            <View style={styles.headerSpacer} />
          </View>
          
          {selectedJobPost && (
            <ScrollView style={styles.modalContent}>
              <View style={styles.jobDetailsCard}>
                {/* Job Overview */}
                <View style={styles.jobDetailsSection}>
                  <Text style={styles.jobDetailsSectionTitle}>Job Overview</Text>
                  <View style={styles.jobDetailsGrid}>
                    <View style={styles.jobDetailsItem}>
                      <Text style={styles.jobDetailsLabel}>Department</Text>
                      <Text style={styles.jobDetailsValue}>{selectedJobPost.department}</Text>
                    </View>
                    <View style={styles.jobDetailsItem}>
                      <Text style={styles.jobDetailsLabel}>Location</Text>
                      <Text style={styles.jobDetailsValue}>{selectedJobPost.location}</Text>
                    </View>
                    <View style={styles.jobDetailsItem}>
                      <Text style={styles.jobDetailsLabel}>Type</Text>
                      <Text style={styles.jobDetailsValue}>{selectedJobPost.type}</Text>
                    </View>
                    <View style={styles.jobDetailsItem}>
                      <Text style={styles.jobDetailsLabel}>Budget</Text>
                      <Text style={styles.jobDetailsValue}>£{selectedJobPost.budget.toLocaleString()}</Text>
                    </View>
                  </View>
                </View>

                {/* Performance Metrics */}
                <View style={styles.jobDetailsSection}>
                  <Text style={styles.jobDetailsSectionTitle}>Performance Metrics</Text>
                  <View style={styles.performanceMetricsGrid}>
                    <View style={styles.performanceMetricCard}>
                      <Text style={styles.performanceMetricNumber}>{selectedJobPost.applications}</Text>
                      <Text style={styles.performanceMetricLabel}>Applications</Text>
                    </View>
                    <View style={styles.performanceMetricCard}>
                      <Text style={styles.performanceMetricNumber}>{selectedJobPost.views}</Text>
                      <Text style={styles.performanceMetricLabel}>Views</Text>
                    </View>
                    <View style={styles.performanceMetricCard}>
                      <Text style={styles.performanceMetricNumber}>{selectedJobPost.conversionRate}%</Text>
                      <Text style={styles.performanceMetricLabel}>Conversion</Text>
                    </View>
                    <View style={styles.performanceMetricCard}>
                      <Text style={styles.performanceMetricNumber}>{selectedJobPost.hires}</Text>
                      <Text style={styles.performanceMetricLabel}>Hires</Text>
                    </View>
                  </View>
                </View>

                {/* Application Breakdown */}
                <View style={styles.jobDetailsSection}>
                  <Text style={styles.jobDetailsSectionTitle}>Application Breakdown</Text>
                  <View style={styles.applicationBreakdown}>
                    <View style={styles.breakdownItem}>
                      <View style={[styles.breakdownIndicator, { backgroundColor: '#10b981' }]} />
                      <Text style={styles.breakdownLabel}>Hired</Text>
                      <Text style={styles.breakdownValue}>{selectedJobPost.hires}</Text>
                    </View>
                    <View style={styles.breakdownItem}>
                      <View style={[styles.breakdownIndicator, { backgroundColor: '#3b82f6' }]} />
                      <Text style={styles.breakdownLabel}>Interviewed</Text>
                      <Text style={styles.breakdownValue}>{selectedJobPost.interviews}</Text>
                    </View>
                    <View style={styles.breakdownItem}>
                      <View style={[styles.breakdownIndicator, { backgroundColor: '#f59e0b' }]} />
                      <Text style={styles.breakdownLabel}>Shortlisted</Text>
                      <Text style={styles.breakdownValue}>{selectedJobPost.shortlisted}</Text>
                    </View>
                    <View style={styles.breakdownItem}>
                      <View style={[styles.breakdownIndicator, { backgroundColor: '#ef4444' }]} />
                      <Text style={styles.breakdownLabel}>Rejected</Text>
                      <Text style={styles.breakdownValue}>{selectedJobPost.rejected}</Text>
                    </View>
                    <View style={styles.breakdownItem}>
                      <View style={[styles.breakdownIndicator, { backgroundColor: '#6b7280' }]} />
                      <Text style={styles.breakdownLabel}>Pending</Text>
                      <Text style={styles.breakdownValue}>{selectedJobPost.pending}</Text>
                    </View>
                  </View>
                </View>

                {/* Source Breakdown */}
                <View style={styles.jobDetailsSection}>
                  <Text style={styles.jobDetailsSectionTitle}>Application Sources</Text>
                  <View style={styles.sourceBreakdown}>
                    {Object.entries(selectedJobPost.sourceBreakdown).map(([source, count]) => (
                      <View key={source} style={styles.sourceItem}>
                        <Text style={styles.sourceLabel}>
                          {source.charAt(0).toUpperCase() + source.slice(1)}
                        </Text>
                        <View style={styles.sourceBar}>
                          <View 
                            style={[
                              styles.sourceBarFill,
                              { width: `${(count / selectedJobPost.applications) * 100}%` }
                            ]} 
                          />
                        </View>
                        <Text style={styles.sourceCount}>{count}</Text>
                      </View>
                    ))}
                  </View>
                </View>

                {/* Skills Match */}
                <View style={styles.jobDetailsSection}>
                  <Text style={styles.jobDetailsSectionTitle}>Skills Match Distribution</Text>
                  <View style={styles.skillsMatch}>
                    {Object.entries(selectedJobPost.skillsMatch).map(([level, count]) => (
                      <View key={level} style={styles.skillsMatchItem}>
                        <View style={[
                          styles.skillsMatchIndicator,
                          { backgroundColor: 
                            level === 'excellent' ? '#10b981' :
                            level === 'good' ? '#3b82f6' :
                            level === 'fair' ? '#f59e0b' : '#ef4444'
                          }
                        ]} />
                        <Text style={styles.skillsMatchLabel}>
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </Text>
                        <Text style={styles.skillsMatchCount}>{count}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            </ScrollView>
          )}
        </SafeAreaView>
      </LinearGradient>
    </Modal>
  );

  if (isLoading) {
    return (
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.fullScreenContainer}>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <SafeAreaView style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#FFFFFF" />
          <Text style={styles.loadingText}>Loading analytics...</Text>
        </SafeAreaView>
      </LinearGradient>
    );
  }

  return (
    <LinearGradient colors={['#4A90E2', '#4A90E2']} style={styles.fullScreenContainer}>
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      <SafeAreaView style={styles.safeContainer}>
        <Animated.View style={[{ flex: 1 }, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
          {renderHeader()}
          
          <View style={styles.contentContainer}>
            <ScrollView 
              style={styles.content}
              refreshControl={
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
              }
              showsVerticalScrollIndicator={false}
            >
              {renderOverviewMetrics()}
              {renderSimpleChart()}
              {renderJobPostsPerformance()}
              {renderInsights()}
              {renderBenchmarks()}
            </ScrollView>
          </View>

          {/* Floating Action Button */}
          <TouchableOpacity 
            style={styles.fab}
            onPress={() => navigateToScreen('CreateReportScreen')}
          >
            <MaterialCommunityIcons name="file-chart" size={24} color="#fff" />
          </TouchableOpacity>
        </Animated.View>

        {renderJobPostDetailsModal()}
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 16,
  },
  header: {
    paddingTop: 10,
    paddingBottom: 15,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  filterButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeframeSelector: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    padding: 4,
  },
  timeframeButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  activeTimeframeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  timeframeButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  activeTimeframeButtonText: {
    color: '#fff',
    fontWeight: '600',
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
  },
  
  // Metrics styles
  metricsContainer: {
    padding: 20,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  metricCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    width: (width - 52) / 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  metricIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  metricNumber: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  metricSubtext: {
    fontSize: 12,
    color: '#94A3B8',
  },
  additionalMetrics: {
    flexDirection: 'row',
    gap: 12,
  },
  additionalMetricCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  additionalMetricNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
    marginTop: 4,
  },
  additionalMetricLabel: {
    fontSize: 11,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 2,
  },

  // Chart styles
  chartContainer: {
    padding: 20,
    paddingTop: 0,
  },
  chartSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 20,
  },
  simpleChart: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  chartBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 120,
    marginBottom: 16,
  },
  chartBarContainer: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 2,
  },
  chartBar: {
    width: '80%',
    borderRadius: 4,
    marginBottom: 8,
  },
  chartBarLabel: {
    fontSize: 10,
    color: '#64748B',
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  chartLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  chartLegendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 6,
  },
  chartLegendText: {
    fontSize: 12,
    color: '#64748B',
  },

  // Performance styles
  performanceContainer: {
    padding: 20,
    paddingTop: 0,
  },
  performanceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewAllButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4A90E2',
    marginRight: 4,
  },
  jobPostCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  jobPostHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  jobPostTitleContainer: {
    flex: 1,
  },
  jobPostTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  jobPostDepartment: {
    fontSize: 14,
    color: '#64748B',
  },
  jobPostBadges: {
    alignItems: 'flex-end',
    gap: 4,
  },
  performanceBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  performanceBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#fff',
  },
  urgencyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  urgencyBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#fff',
  },
  jobPostMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  jobPostMetric: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  jobPostMetricText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  jobPostProgress: {
    marginBottom: 12,
  },
  progressInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
  },
  progressText: {
    fontSize: 12,
    color: '#4A90E2',
  },
  progressBar: {
    height: 4,
    backgroundColor: '#E5E7EB',
    borderRadius: 2,
  },
  progressFill: {
    height: 4,
    backgroundColor: '#4A90E2',
    borderRadius: 2,
  },
  jobPostFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  jobPostDate: {
    fontSize: 12,
    color: '#94A3B8',
  },
  jobPostLocation: {
    fontSize: 12,
    color: '#94A3B8',
  },

  // Insights styles
  insightsContainer: {
    padding: 20,
    paddingTop: 0,
  },
  insightCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    position: 'relative',
  },
  insightIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  insightContent: {
    flex: 1,
  },
  insightTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  insightMessage: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  priorityIndicator: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  insightAction: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 12,
  },
  insightActionText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4A90E2',
  },

  // Benchmarks styles
  benchmarksContainer: {
    padding: 20,
    paddingTop: 0,
    paddingBottom: 100,
  },
  benchmarkGrid: {
    gap: 16,
  },
  benchmarkCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  benchmarkMetric: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  benchmarkComparison: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  benchmarkValue: {
    flex: 1,
  },
  benchmarkLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  benchmarkNumber: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
  },
  benchmarkIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  benchmarkStatus: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },

  // Modal styles
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 20,
  },
  modalBackButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  modalContent: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
  },
  jobDetailsCard: {
    margin: 20,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  jobDetailsSection: {
    marginBottom: 24,
  },
  jobDetailsSectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 8,
  },
  jobDetailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  jobDetailsItem: {
    width: (width - 80) / 2,
  },
  jobDetailsLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  jobDetailsValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
  },
  performanceMetricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  performanceMetricCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 12,
    width: (width - 104) / 2,
    alignItems: 'center',
  },
  performanceMetricNumber: {
    fontSize: 20,
    fontWeight: '700',
    color: '#4A90E2',
  },
  performanceMetricLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  applicationBreakdown: {
    gap: 12,
  },
  breakdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  breakdownIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  breakdownLabel: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
  },
  breakdownValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
  },
  sourceBreakdown: {
    gap: 12,
  },
  sourceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sourceLabel: {
    fontSize: 14,
    color: '#64748B',
    width: 80,
  },
  sourceBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
  },
  sourceBarFill: {
    height: 8,
    backgroundColor: '#4A90E2',
    borderRadius: 4,
  },
  sourceCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    width: 30,
    textAlign: 'right',
  },
  skillsMatch: {
    gap: 12,
  },
  skillsMatchItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  skillsMatchIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  skillsMatchLabel: {
    flex: 1,
    fontSize: 14,
    color: '#64748B',
  },
  skillsMatchCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
  },

  // FAB styles
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#4A90E2',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});